import React, { FC, useEffect, useState } from 'react'
import { doc, onSnapshot, Unsubscribe } from 'firebase/firestore'

import { RiderHeaderInfo } from '../components/RiderHeaderInfo/RiderHeaderInfo'
import { CustomTabs } from '../components/CustomTabs/CustomTabs'
import { ClassesTab } from '../components/tabs/ClassesTab/ClassesTab'
import { FeesTab } from '../components/tabs/FeesTab/FeesTab'
import { TicketsTab } from '../components/tabs/TicketsTab/TicketsTab'
import { PaperworkTab } from '../components/tabs/PaperworkTab/PaperworkTab'
import { TeamTab } from '../components/tabs/TeamTab/TeamTab'
import { ProfileTab } from '../components/tabs/ProfileTab/ProfileTab'
import { HorsesTab } from '../components/tabs/HorsesTab/HorsesTab'
import { OverviewTab } from '../components/tabs/OverviewTab/OverviewTab'
import { DrawerModal } from '../../common/DrawerModal'
import { QATab } from '../components/tabs/QATab/QATab'
import ActivityTab from '../components/tabs/ActivityTab/ActivityTab'

import FirebaseApp from '../../../../services/firebaseApp'
import FirestoreService from '../../../../services/firestoreService'
import UserService from '../../../../services/firebase-service/users/userService'
import EventRegisteredUsersService from '../../../../services/firebase-service/event-registered-users/EventRegisteredUsersService'

import { getRegistrationEventDataInDb } from '../../../../helpers/registration-on-event'
import { getConvertedData } from '../../../../models/interface.helper'

import { EventReviewPublishModel } from '../../../../models/event-review-publish/event-review-publish.model'
import { UserModel } from '../../../../models/users/user.model'

import { IUserInterface } from '../../../../models/users/user.interface'
import { IRegistrationByDayInterface } from '../../../../models/registrations-by-day/registrationByDay.interface'
import { IEventReviewPublish } from '../../../../models/event-review-publish/event-review-publish.interface'
import { TEventRegisteredUsers } from '../../../../models/event-registered-users/event-registered-users.interface'

import { CONST } from '../../../../const/const'
import { REGISTRAR_TABS } from '../RegistrarInfoModal/RegistrarInfoModal'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

export const RIDER_TABS = {
  OVERVIEW: { value: 'overview' },
  ACTIVITY: { value: 'activity' },
  PROFILE: { value: 'profile' },
  CLASSES: { value: 'classes' },
  FEES: { value: 'fees' },
  TICKETS: { value: 'tickets' },
  PAPERWORK: { value: 'paperwork' },
  TEAM: { value: 'team' },
  HORSES: { value: 'horses' },
  QA: { value: 'q&a' },
}

interface RiderInfoModalProps {
  dataToPassOn: {
    registrarId: string
    riderId: string
    registrationId: string
    eventId: string
    defaultActiveTab?: string
    onClose?: () => Promise<void>
  }
}

export const RiderInfoModal: FC<RiderInfoModalProps> = ({ dataToPassOn }) => {
  const { registrarId, riderId, registrationId, eventId, defaultActiveTab, onClose } = dataToPassOn

  const [loading, setLoading] = useState(true)
  const [rider, setRider] = useState<IUserInterface | null>(null)
  const [organizer, setOrganizer] = useState<IUserInterface | null>(null)
  const [activeTab, setActiveTab] = useState(defaultActiveTab || RIDER_TABS.OVERVIEW.value)
  const [riders, setRiders] = useState<IRegistrationByDayInterface[]>([])

  const [publishedEvent, setPublishedEvent] = useState<IEventReviewPublish | null>(null)
  const [eventRegisteredUser, setEventRegisteredUser] = useState<TEventRegisteredUsers | null>(null)

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true)

        const { registrationsByDay } = await getRegistrationEventDataInDb({
          registrationId,
          ownerId: riderId,
          type: ['classes'],
        })

        const publishedEventSnapShot = await FirestoreService.getItem(
          COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
          eventId
        )
        let publishedEvent_ =
          EventReviewPublishModel.fromFirestoreDoc(publishedEventSnapShot).toObject()

        const user = await UserService.getUserById(riderId!)
        const organiserInDb = await UserService.getUserById(publishedEvent_.owner!)
        const eventRegisteredUser_ = await EventRegisteredUsersService.getById(registrationId)

        setRider(user)
        setOrganizer(organiserInDb)
        setRiders(registrationsByDay)
        setPublishedEvent(publishedEvent_)
        setEventRegisteredUser(eventRegisteredUser_)
      } catch (error) {
        console.error(error, 'error')
      } finally {
        setLoading(false)
      }
    }
    getData().then()
  }, [eventId, riderId, registrarId, registrationId])

  useEffect(() => {
    let unsubscribe: Unsubscribe | null = null

    if (riderId) {
      unsubscribe = onSnapshot(
        doc(FirebaseApp.firestore, COLLECTIONS.USERS.NAME, riderId),
        (doc) => {
          if (doc.exists()) {
            setRider(getConvertedData(UserModel.fromFirestoreDoc(doc).toObject()))
          }
        }
      )
    }

    return () => {
      unsubscribe?.()
    }
  }, [riderId])

  return (
    <DrawerModal onClose={async () => onClose && (await onClose())} type="RIDER_INFO">
      <div className="w-full text-SeabiscuitDark200ThemeColor max-w-6xl mx-auto px-4 py-10 md:py-20 overflow-auto">
        <RiderHeaderInfo
          user={rider}
          eventId={eventId}
          registrarId={registrarId}
          registrationId={registrationId}
          riders={riders}
          loading={loading}
          activeTab={activeTab}
          eventReviewPublish={publishedEvent}
        />
        <CustomTabs
          type="rider"
          color="#F70763"
          setActiveTab={setActiveTab}
          activeTab={activeTab}
          tabs={RIDER_TABS}
          loading={loading}
        />
        <div className="mt-7">
          {activeTab === RIDER_TABS.OVERVIEW.value && (
            <OverviewTab
              user={rider}
              publishedEvent={publishedEvent}
              setActiveTab={setActiveTab}
              riders={riders}
              registrarId={registrarId}
              eventId={eventId}
              organizer={organizer}
              registrationId={registrationId}
              riderId={riderId}
            />
          )}
          {activeTab === RIDER_TABS.PROFILE.value && (
            <ProfileTab user={rider} setUser={setRider} publishedEvent={publishedEvent} />
          )}
          {activeTab === REGISTRAR_TABS.ACTIVITY.value && (
            <ActivityTab type="rider" userId={riderId} />
          )}
          {activeTab === RIDER_TABS.CLASSES.value && (
            <ClassesTab
              registrarId={registrarId}
              registrationId={registrationId}
              eventId={eventId}
              organizer={organizer}
              user={rider}
              publishedEvent={publishedEvent}
            />
          )}
          {activeTab === RIDER_TABS.FEES.value && (
            <FeesTab
              registrarId={registrarId}
              registrationId={registrationId}
              eventId={eventId}
              publishedEvent={publishedEvent}
              user={rider}
              organizer={organizer}
            />
          )}
          {activeTab === RIDER_TABS.TICKETS.value && (
            <TicketsTab
              registrarId={registrarId}
              registrationId={registrationId}
              eventId={eventId}
              publishedEvent={publishedEvent}
              user={rider}
              organizer={organizer}
            />
          )}
          {activeTab === RIDER_TABS.PAPERWORK.value && (
            <PaperworkTab
              registrationsByDay={riders}
              eventId={eventId}
              userId={riderId}
              registrationId={registrationId}
              isLoading={loading}
            />
          )}
          {activeTab === RIDER_TABS.TEAM.value && (
            <TeamTab
              registrationId={registrationId}
              user={rider}
              publishedEvent={publishedEvent}
              isLoading={loading}
            />
          )}
          {activeTab === RIDER_TABS.HORSES.value && (
            <HorsesTab
              registrationsByDay={riders}
              publishedEvent={publishedEvent}
              isLoading={loading}
            />
          )}
          {activeTab === RIDER_TABS.QA.value && (
            <QATab
              eventReviewPublish={publishedEvent}
              eventRegisteredUser={eventRegisteredUser}
              setEventRegisteredUser={setEventRegisteredUser}
              riders={riders}
            />
          )}
        </div>
      </div>
    </DrawerModal>
  )
}
